import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, FileText, Building2, Hash, Calendar, DollarSign, Wallet, Banknote, Receipt, FileSearch } from 'lucide-react';

const fieldConfig = [
  { key: 'tercero', label: 'Tercero', icon: Building2 },
  { key: 'nit', label: 'NIT', icon: Hash },
  { key: 'numero_documento', label: 'N° Documento', icon: Receipt },
  { key: 'valor', label: 'Valor', icon: DollarSign, format: (v) => v != null ? `$${Number(v).toLocaleString('es-CO')}` : null },
  { key: 'fecha', label: 'Fecha', icon: Calendar },
  { key: 'concepto', label: 'Concepto', icon: FileText },
  { key: 'referencia_bancaria', label: 'Referencia Bancaria', icon: Wallet },
  { key: 'banco', label: 'Banco', icon: Banknote },
];

const tipoLabels = {
  comprobante_egreso: 'Comprobante de Egreso',
  cuenta_por_pagar: 'Cuenta por Pagar',
  factura: 'Factura',
  soporte_pago: 'Soporte de Pago',
};

const DocumentDetailsModal = ({ docId, open, onClose }) => {
  const { token, API } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!docId || !open) return;
    setLoading(true);
    setDoc(null);
    axios.get(`${API}/documents/${docId}/details`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => setDoc(res.data.document))
      .catch(() => setDoc(null))
      .finally(() => setLoading(false));
  }, [docId, open, API, token]);

  const getValue = (key) => {
    if (!doc) return null;
    if (doc[key] != null && doc[key] !== '') return doc[key];
    const fromAnalysis = doc.analisis_completo?.[key];
    return fromAnalysis != null && fromAnalysis !== '' ? fromAnalysis : null;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="document-details-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-zinc-900">
            <FileSearch size={20} className="text-indigo-600" />
            Ficha del Documento
          </DialogTitle>
          <DialogDescription className="sr-only">
            Muestra los datos extraídos por la IA del documento seleccionado.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="animate-spin text-zinc-400" size={28} />
          </div>
        ) : !doc ? (
          <p className="text-center text-zinc-500 py-8">No se pudo cargar la información del documento.</p>
        ) : (
          <div className="space-y-4">
            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="text-indigo-600" size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-zinc-900 truncate" title={doc.filename}>{doc.filename}</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {doc.tipo_documento && (
                      <Badge variant="outline" className="text-xs">
                        {tipoLabels[doc.tipo_documento] || doc.tipo_documento}
                      </Badge>
                    )}
                    {doc.status && (
                      <Badge className="text-xs bg-zinc-100 text-zinc-700">
                        {doc.status}
                      </Badge>
                    )}
                    {doc.batch_id && (
                      <Badge className="text-xs bg-violet-100 text-violet-700">en lote</Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">
                Datos extraídos por IA
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {fieldConfig.map(({ key, label, icon: Icon, format }) => {
                  const raw = getValue(key);
                  const value = raw != null && format ? format(raw) : raw;
                  return (
                    <div
                      key={key}
                      className="border border-zinc-200 rounded-lg p-3 bg-white"
                      data-testid={`detail-${key}`}
                    >
                      <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1">
                        <Icon size={14} />
                        <span>{label}</span>
                      </div>
                      <p className={`text-sm font-medium ${value ? 'text-zinc-900' : 'text-zinc-400 italic'}`}>
                        {value || 'No disponible'}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {doc.analisis_completo && (
              <details className="border border-zinc-200 rounded-lg">
                <summary className="px-4 py-2 cursor-pointer text-sm font-medium text-zinc-700 hover:bg-zinc-50">
                  Ver análisis completo de la IA (JSON)
                </summary>
                <pre className="text-xs bg-zinc-900 text-zinc-100 p-3 rounded-b-lg overflow-x-auto max-h-64">
                  {JSON.stringify(doc.analisis_completo, null, 2)}
                </pre>
              </details>
            )}

            {doc.file_hash && (
              <p className="text-[10px] text-zinc-400 font-mono break-all">
                hash: {doc.file_hash}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentDetailsModal;
